import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'gng_cart';

function loadCart(businessId) {
  if (!businessId) return [];
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${businessId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCart(businessId, items) {
  if (!businessId) return;
  try {
    localStorage.setItem(`${STORAGE_KEY}_${businessId}`, JSON.stringify(items));
  } catch (e) {
    console.warn('Failed to persist cart', e);
  }
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [businessId, setBusinessIdState] = useState(null);
  const [items, setItemsState] = useState([]);
  const [appliedReward, setAppliedRewardState] = useState(null); // { claimId, businessId, rewardType, discountPercent?, freeMenuItemId?, label? }

  const setBusinessId = useCallback((id) => {
    if (id === null || id === undefined) {
      setBusinessIdState(null);
      setItemsState([]);
      setAppliedRewardState(null);
      return;
    }
    setBusinessIdState(id);
    setItemsState(loadCart(id));
    setAppliedRewardState((prev) => (prev?.businessId === id ? prev : null));
  }, []);

  useEffect(() => {
    if (businessId != null) {
      saveCart(businessId, items);
    }
  }, [businessId, items]);

  const addItem = useCallback((item) => {
    setItemsState((prev) => [...prev, item]);
  }, []);

  const updateQty = useCallback((lineId, quantity) => {
    if (quantity < 1) {
      setItemsState((prev) => prev.filter((i) => i.id !== lineId));
      return;
    }
    setItemsState((prev) =>
      prev.map((i) => (i.id === lineId ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((lineId) => {
    setItemsState((prev) => prev.filter((i) => i.id !== lineId));
  }, []);

  const clear = useCallback(() => {
    setItemsState([]);
    setAppliedRewardState(null);
  }, []);

  const setAppliedReward = useCallback((reward) => {
    setAppliedRewardState(reward);
  }, []);

  const clearAppliedReward = useCallback(() => {
    setAppliedRewardState(null);
  }, []);

  const PERCENT_OFF_TYPES = ['10_percent_off', 'custom_percent'];
  const subtotalCents = items.reduce((s, i) => s + i.unitPriceCents * i.quantity, 0);
  const discountCents = appliedReward && appliedReward.businessId === businessId && PERCENT_OFF_TYPES.includes(appliedReward.rewardType)
    ? Math.round(subtotalCents * ((appliedReward.discountPercent ?? 10) / 100))
    : 0;
  const totalCents = Math.max(0, subtotalCents - discountCents);

  const value = {
    businessId,
    items,
    setBusinessId,
    addItem,
    updateQty,
    removeItem,
    clear,
    itemCount: items.reduce((s, i) => s + i.quantity, 0),
    appliedReward,
    setAppliedReward,
    clearAppliedReward,
    subtotalCents,
    discountCents,
    totalCents,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
